import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import type { RouteComponentProps } from "react-router-dom";
import { isDirty } from "redux-form";
import invariant from "tiny-invariant";

import { eraseStateProp } from "~/actions/dashboard";
import { getPatientId } from "~/actions/get_patient_id";
import {
  patientIsSavingSuccess,
  type TCreatePatientInstructions,
  updateAndSubmitPatient,
  updatePatient,
} from "~/actions/post_patient";
import { FileType, RxType } from "~/common/constants";
import {
  is3DPROCourse,
  isAdditionalAlignersCourse,
  isRetainersCourse,
  Status,
} from "~/common/courses";
import { convertToJSONFullRx } from "~/common/instructions";
import { remoteLog } from "~/common/logging";
import { assertIsPatient, getLastCorrection, isPatient } from "~/common/patient";
import { Material } from "~/common/prescription";
import { scrollTo } from "~/components/common/ScrollToElement/scrollTo";
import { MaterialOptions } from "~/components/patient/patient_shared/material_options";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { getCourseInstallment, selectCourseInstallment } from "~/slices/course-installment";
import { createInstructionsSuccess, type TInstructions } from "~/slices/instructions";
import {
  selectAdditionalAlignersFormValues,
  selectCorrectionFormValues,
  selectLinksFormValues,
} from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TNone } from "~/types/common";
import type { TPatient } from "~/types/patient";

import LinksCT from "./addLinks";
import { PatientUpdateAdditionalAligners } from "./patient_new/patient_new_additional_aligners";
import { PatientNewImages } from "./patient_new/patient_new_images";
import {
  type TPatientNewRequiredField,
  validateReqiredFields,
  validateRequiredDraftFields,
} from "./patient_new_validation";
import PatientUpdateCourse from "./patient_update/patient_update_course";
import PatientUpdateDoctorInfo from "./patient_update/patient_update_doctor_info";
import { PatientUpdateFiles } from "./patient_update/patient_update_files";
import PatientUpdatePatientInfo from "./patient_update/patient_update_patient_info";
import PatientUpdatePayment from "./patient_update/patient_update_payment";
import PatientUpdateUploadCT from "./patient_update/patient_update_upload_ct";
import RecipeForm from "./recipe_redux_form";
import RecipeFormShortRX from "./recipe_redux_form_short_rx";
import RouteLeavingGuard from "./route_leaving_guard";

const mapStateToProps = (state: RootState) => {
  return {
    user: state.user,
    patient: state.patient,
    patientSaving: state.patientSaving,
    instructions: state.instructions,
    media: state.media,
    media_s3: state.media_s3,
    formValues: selectCorrectionFormValues(state),
    additionalAlignersFormValues: selectAdditionalAlignersFormValues(state),
    links: selectLinksFormValues(state),
    dirtyCorrection: isDirty("correction")(state),
    dirtyLinks: isDirty("links")(state),
    courseInstallment: selectCourseInstallment(state),
  };
};

const mapDispatchToProps = {
  updateAndSubmitPatient,
  getPatientId,
  patientIsSaving: patientIsSavingSuccess,
  initInstrucrions: createInstructionsSuccess,
  eraseStateProp,
  updatePatient,
  getCourseInstallment,
};

type PatientUpdateProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
  }>;

type PatientUpdateState = {
  isSubmitPending: boolean;
  isDraftPending: boolean;
  isDirty: boolean;
  submit: boolean;
};

class PatientUpdate extends Component<PatientUpdateProps, PatientUpdateState> {
  constructor(props: PatientUpdateProps) {
    super(props);
    this.state = {
      isSubmitPending: false,
      isDraftPending: false,
      isDirty: false,
      submit: false,
    };
    this.submitButton = this.submitButton.bind(this);
    this.submitSaveButton = this.submitSaveButton.bind(this);
    this.setDirty = this.setDirty.bind(this);
    this.renderRx = this.renderRx.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_update");
  }

  async submitButton() {
    assertIsPatient(this.props.patient);

    const { user, patient, instructions, formValues, additionalAlignersFormValues } = this.props;
    const course_id = instructions.course_id ?? patient.course.course_id;
    const lastCorrection = getLastCorrection(patient);
    const rxTypeId = lastCorrection.prescription?.rx_type_id ?? patient.rx_type_id;
    const emptyFields: TPatientNewRequiredField[] = [];

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => Object.values(link));

    const areRequiredFieldsValid = validateReqiredFields({
      user,
      patient,
      links,
      instructions,
      formValues,
      additionalAlignersFormValues,
      media: this.props.media,
      emptyFields,
    });

    const areConditionsSatisfied =
      areRequiredFieldsValid ||
      lastCorrection.index > 0 ||
      ((isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id)) &&
        !lastCorrection.order_options.full_edit) ||
      (lastCorrection.index == 0 && lastCorrection.approved_plan_id);

    if (!areConditionsSatisfied) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      scrollTo({ id: emptyFields[0]!, duration: 1500 });
      return;
    }

    this.setState({ isSubmitPending: true, submit: true });

    if (patient.status == Status.UNFILLED_CASE) {
      invariant(formValues, "expected formValues to be defined");
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };

      instructionsCopy.prescription = convertToJSONFullRx(formValues);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
      instructionsCopy.links = links;
      instructionsCopy.additional_aligners = additionalAlignersFormValues?.aligners;

      try {
        await this.props.updateAndSubmitPatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
      } catch {
        this.setState({ isSubmitPending: false });
      }
    } else if (
      patient.course.correction.length > 1 &&
      lastCorrection.order_options.can_edit_prescription &&
      lastCorrection.order_options.full_edit
    ) {
      invariant(formValues, "expected formValues to be defined");
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };

      instructionsCopy.prescription = convertToJSONFullRx(formValues);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
      instructionsCopy.links = links;

      try {
        await this.props.updatePatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
      } catch {
        this.setState({ isSubmitPending: false });
      }
    } else if (!lastCorrection.order_options.can_edit_prescription) {
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };
      instructionsCopy.links = links;

      try {
        await this.props.updatePatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
      } catch {
        this.setState({ isSubmitPending: false });
      }
    } else {
      invariant(formValues, "expected formValues to be defined");
      const instructionsCopy: TCreatePatientInstructions = { ...instructions };

      instructionsCopy.prescription = convertToJSONFullRx(formValues);
      instructionsCopy.prescription.rx_type_id = rxTypeId;
      instructionsCopy.links = links;
      instructionsCopy.additional_aligners = additionalAlignersFormValues?.aligners;

      try {
        await this.props.updatePatient(patient.patient_id, {
          instructions: instructionsCopy,
          media: this.props.media,
          media_s3: this.props.media_s3,
        });
      } catch {
        this.setState({ isSubmitPending: false });
      }
    }
  }

  async submitSaveButton() {
    assertIsPatient(this.props.patient);
    invariant(this.props.formValues, "expected formValues to be defined");

    const { patient, instructions, formValues } = this.props;
    const lastCorrection = getLastCorrection(patient);
    const rxTypeId = lastCorrection.prescription?.rx_type_id ?? patient.rx_type_id;
    const emptyFields: TPatientNewRequiredField[] = [];

    const linksArray = this.props.links?.links ?? [];
    const links = linksArray.flatMap((link) => Object.values(link));

    const areRequiredFieldsValid = validateRequiredDraftFields({
      links,
      instructions,
      formValues,
      emptyFields,
    });

    if (!areRequiredFieldsValid) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      scrollTo({ id: emptyFields[0]!, duration: 1500 });
      return;
    }

    const instructionsCopy: TCreatePatientInstructions = { ...instructions };

    instructionsCopy.prescription = convertToJSONFullRx(formValues);
    instructionsCopy.prescription.rx_type_id = rxTypeId;
    instructionsCopy.links = links;

    this.setState({ isDraftPending: true, submit: true });

    try {
      await this.props.updatePatient(patient.patient_id, {
        instructions: instructionsCopy,
        media: this.props.media,
        media_s3: this.props.media_s3,
      });
    } catch {
      this.setState({ isDraftPending: false });
    }
  }

  componentDidMount() {
    const { patient_id } = this.props.match.params;
    window.scrollTo(0, 0);

    void this.props.getPatientId(Number(patient_id));
    void this.props.getCourseInstallment(Number(patient_id));

    this.props.initInstrucrions();
    this.props.eraseStateProp("media");

    setDocumentTitle(this.props.intl.formatMessage({ id: "BTN_EDIT" }), patient_id);
  }

  UNSAFE_componentWillUpdate(nextProps: PatientUpdateProps) {
    if (nextProps.patient && nextProps.patientSaving) {
      this.props.patientIsSaving(false);
      this.props.eraseStateProp("patient");
      this.props.history.push("/pages/patients");
    }
  }

  componentDidUpdate() {
    const { dirtyCorrection, dirtyLinks, media, media_s3 } = this.props;
    const { isDirty, submit } = this.state;
    const formIsDirty =
      !submit &&
      (isDirty ||
        dirtyCorrection ||
        dirtyLinks ||
        Object.keys(media).length >= 1 ||
        (media_s3.files && media_s3.files.length >= 1));
    if (formIsDirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  }

  componentWillUnmount() {
    this.props.eraseStateProp("patient");
    window.onbeforeunload = null;
  }

  setDirty<T extends keyof TInstructions>(key: T, data?: TInstructions[T]) {
    const patient = this.props.patient as TPatient;

    if (
      data == "" ||
      (key in patient && data !== patient[key as keyof TPatient]) ||
      (key in patient.course && data !== patient.course[key as keyof TPatient["course"]]) ||
      (key in patient.clinic && data !== patient.clinic[key as keyof TPatient["clinic"]])
    ) {
      this.setState({ isDirty: true });
    } else {
      this.setState({ isDirty: false });
    }
  }

  renderRx(rxTypeId: RxType, course_id: number | TNone) {
    if (rxTypeId === RxType.STANDARD) {
      return <RecipeForm course_id={course_id} />;
    } else {
      return <RecipeFormShortRX course_id={course_id} />;
    }
  }

  render() {
    const { isDirty, submit } = this.state;
    const {
      patient,
      instructions,
      formValues,
      dirtyCorrection,
      dirtyLinks,
      media,
      media_s3,
      courseInstallment,
    } = this.props;

    if (!isPatient(patient) || !courseInstallment) {
      return (
        <Layout>
          <Loader />
        </Layout>
      );
    }

    const lastCorrection = getLastCorrection(patient);
    const rxTypeId = lastCorrection.prescription?.rx_type_id ?? patient.rx_type_id;
    const { full_edit, can_edit_prescription } = lastCorrection.order_options;
    const course_id = instructions.course_id ?? patient.course.course_id;
    const isUnfilledCase = patient.status == Status.UNFILLED_CASE;
    const disable_on_3d_smile_pro = isUnfilledCase && is3DPROCourse(patient.course.course_id);
    const has_medit_files =
      patient.s3_media.find((f) => f.file_type == FileType.MEDIT) !== undefined;
    const material = formValues?.material;

    const formIsDirty =
      !submit &&
      (isDirty ||
        dirtyCorrection ||
        dirtyLinks ||
        Object.keys(media).length >= 1 ||
        (media_s3.files && media_s3.files.length >= 1));

    return (
      <Layout>
        <Portlet as="main">
          <PortletTitle iconClassName="icon-book-open" displayRequiredFieldsInfo>
            <FormattedMessage id="BTN_EDIT" />
          </PortletTitle>

          <RouteLeavingGuard
            when={formIsDirty}
            shouldBlockNavigation={() => formIsDirty}
            saveChanges={isUnfilledCase ? this.submitSaveButton : this.submitButton}
            saveAsADraft={false}
          />

          <div className="portlet-body form" id="add-patient-form">
            <div className="form-body">
              <div className="alert alert-danger display-hide">
                <button className="close" data-close="alert" />
                <span id="error-msg" />
              </div>
              <div className="row">
                <div className="col-md-5">
                  <PatientUpdateDoctorInfo setDirty={this.setDirty} patient={patient} />
                </div>
                {has_medit_files ? (
                  <div className="col-xs-10 col-sm-5">
                    <div
                      className="alert alert-success alert-dismissible show"
                      role="alert"
                      id="upload-medit-alert-warning"
                    >
                      <FormattedMessage id="th.medit.file.upload_success" />
                      <button
                        type="button"
                        className="close"
                        data-dismiss="alert"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              {full_edit ? (
                <>
                  <PatientUpdatePayment
                    patient={patient}
                    courseInstallment={courseInstallment}
                    setDirty={this.setDirty}
                  />
                  <PatientUpdatePatientInfo setDirty={this.setDirty} patient={patient} />
                  <PatientUpdateCourse
                    patient={patient}
                    courseInstallment={courseInstallment}
                    setDirty={this.setDirty}
                  />
                  {isAdditionalAlignersCourse(course_id) ? (
                    <PatientUpdateAdditionalAligners
                      maxStage={patient.original_patient?.steps_count_completed as number}
                    />
                  ) : null}
                  {rxTypeId === RxType.STANDARD ? (
                    <RecipeForm course_id={course_id} />
                  ) : (
                    <RecipeFormShortRX course_id={course_id} />
                  )}
                </>
              ) : null}

              {can_edit_prescription && !full_edit && !isAdditionalAlignersCourse(course_id) ? (
                <MaterialOptions />
              ) : null}

              {(
                isRetainersCourse(course_id)
                  ? material == Material.SCAN
                  : !isAdditionalAlignersCourse(course_id)
              ) ? (
                <PatientUpdateFiles
                  patient={patient}
                  required={isRetainersCourse(course_id) ? material == Material.SCAN : false}
                  showUploadedFiles
                  has_medit_files={has_medit_files}
                />
              ) : null}

              {isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : (
                <>
                  <PatientUpdateUploadCT showUploadedFiles />
                  <LinksCT />
                </>
              )}

              {isUnfilledCase &&
              !isRetainersCourse(course_id) &&
              !isAdditionalAlignersCourse(course_id) ? (
                <PatientNewImages patient_id={patient.patient_id} />
              ) : null}

              {can_edit_prescription && !full_edit && !isAdditionalAlignersCourse(course_id)
                ? this.renderRx(rxTypeId, course_id)
                : null}
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-border-t tw-border-[#e7ecf1] tw-py-5 sm:tw-gap-2">
              <LoadingButton
                id="submit-pacient-btn"
                type="button"
                variant="primary"
                size="xl"
                className="tw-w-full sm:tw-max-w-[180px]"
                disabled={disable_on_3d_smile_pro || this.state.isDraftPending}
                onClick={() => this.submitButton()}
                isLoading={this.state.isSubmitPending}
              >
                <FormattedMessage id={isUnfilledCase ? "BTN_SUBMIT_NEW" : "BTN_SAVE"} />
              </LoadingButton>

              {isUnfilledCase && !isAdditionalAlignersCourse(course_id) ? (
                <LoadingButton
                  id="save-pacient-btn"
                  type="button"
                  variant="secondary"
                  size="xl"
                  className="tw-w-full sm:tw-max-w-[180px]"
                  disabled={this.state.isSubmitPending}
                  onClick={() => this.submitSaveButton()}
                  isLoading={this.state.isDraftPending}
                >
                  <FormattedMessage id="BTN_SAVE_DRAFT" />
                </LoadingButton>
              ) : null}
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientUpdate));
